import "./styles.css";
import {
  buildFeedbackPayload,
  buildGenerationPayload,
  type GenerationBrief
} from "./logic.js";

type Sample = {
  id: string;
  title: string;
  coverCopy: string;
  platform: string;
  source: string;
  sourceDate: string | null;
  metric: number | null;
  metricLabel: string;
  visualGroup: string | null;
  hookGroup: string | null;
  coverObjectKey: string | null;
};

type Candidate = {
  id: string;
  sourceId: string;
  title: string;
  coverCopy: string;
  clickReason: string;
  evidence: string[];
  fit: "本稿关联" | "延展选题";
  riskNotes: string[];
  rating: number;
  selected: boolean;
  edited: Record<string, unknown>;
};

type GenerationJob = {
  id: string;
  status: "queued" | "running" | "complete" | "partial" | "error" | "cancelled";
  error: string | null;
  candidates: Candidate[];
};

type CurrentUser = {
  id: string;
  phone: string;
  displayName: string;
  role: "admin" | "editor";
  authDisabled?: boolean;
};

type ManagedUser = {
  id: string;
  name: string;
  phone: string;
  role: "admin" | "editor";
  status: "active" | "disabled";
  createdAt: string;
};

class AuthenticationRequired extends Error {}

const $ = <T extends Element>(selector: string) => {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`页面缺少元素：${selector}`);
  return element;
};

const sampleGrid = $("#sample-grid");
const results = $("#results");
const statusPanel = $("#status-panel");
const generateButton = $<HTMLButtonElement>("#generate");
const loginDialog = $<HTMLDialogElement>("#login-dialog");
const usersDialog = $<HTMLDialogElement>("#users-dialog");
const healthLabel = $("#health-label");
const healthDot = $(".status-dot");
const selectedIds = new Set<string>();
let samples: Sample[] = [];

async function request<T>(
  url: string,
  init?: RequestInit,
  allowUnauthorized = false
): Promise<T> {
  const response = await fetch(url, {
    credentials: "same-origin",
    ...init,
    headers: {
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...init?.headers
    }
  });
  if (response.status === 401 && !allowUnauthorized) {
    throw new AuthenticationRequired();
  }
  const payload = (await response.json()) as T & { error?: string };
  if (!response.ok) {
    throw new Error(payload.error || `请求失败：HTTP ${response.status}`);
  }
  return payload;
}

function setStatus(message: string, error = false) {
  statusPanel.textContent = message;
  statusPanel.classList.toggle("is-error", error);
}

function showLogin() {
  if (!loginDialog.open) loginDialog.showModal();
}

async function loadCurrentUser() {
  const accountLabel = $("#account-label");
  const loginButton = $<HTMLButtonElement>("#login-button");
  const logoutButton = $<HTMLButtonElement>("#logout-button");
  const usersButton = $<HTMLButtonElement>("#users-button");
  try {
    const user = await request<CurrentUser>("/api/auth/me");
    if (user.authDisabled) {
      accountLabel.textContent = "本地开发模式";
      loginButton.hidden = true;
      logoutButton.hidden = true;
      usersButton.hidden = true;
    } else {
      accountLabel.textContent = `${user.displayName} · ${user.phone}`;
      loginButton.hidden = true;
      logoutButton.hidden = false;
      usersButton.hidden = user.role !== "admin";
    }
  } catch (error) {
    if (error instanceof AuthenticationRequired) {
      accountLabel.textContent = "未登录";
      loginButton.hidden = false;
      logoutButton.hidden = true;
      usersButton.hidden = true;
      showLogin();
      return;
    }
    accountLabel.textContent = "账号状态不可用";
  }
}

function showUserMessage(message: string, error = false) {
  const node = $<HTMLElement>("#user-form-message");
  node.textContent = message;
  node.hidden = false;
  node.classList.toggle("is-success", !error);
}

async function loadUsers() {
  const list = $("#users-list");
  list.replaceChildren();
  const payload = await request<{ items: ManagedUser[] }>("/api/admin/users");
  $("#users-count").textContent = `${payload.items.length} 个账号`;
  if (payload.items.length === 0) {
    const empty = document.createElement("p");
    empty.className = "empty-state";
    empty.textContent = "还没有开通账号。";
    list.append(empty);
    return;
  }

  for (const user of payload.items) {
    const row = document.createElement("article");
    row.className = "managed-user";
    row.dataset.status = user.status;
    row.dataset.role = user.role;

    const identity = document.createElement("div");
    identity.className = "managed-user-identity";
    const name = document.createElement("strong");
    name.textContent = user.name;
    const phone = document.createElement("span");
    phone.textContent = user.phone;
    identity.append(name, phone);

    const role = document.createElement("span");
    role.className = `role-stamp role-${user.role}`;
    role.textContent = user.role === "admin" ? "管理员" : "编辑";
    const status = document.createElement("span");
    status.className = `status-stamp status-${user.status}`;
    status.textContent = user.status === "active" ? "已启用" : "已停用";

    const reset = document.createElement("div");
    reset.className = "password-reset";
    const password = document.createElement("input");
    password.type = "password";
    password.minLength = 8;
    password.autocomplete = "new-password";
    password.placeholder =
      user.role === "admin" ? "管理员账号不可重置" : "新密码（至少8位）";
    password.setAttribute("aria-label", `为 ${user.name} 设置新密码`);
    const resetButton = document.createElement("button");
    resetButton.type = "button";
    resetButton.textContent = user.role === "admin" ? "不可重置" : "重置密码";
    if (user.role === "admin") {
      password.disabled = true;
      resetButton.disabled = true;
    }
    resetButton.addEventListener("click", async () => {
      if (password.value.length < 8) {
        showUserMessage("新密码至少需要 8 个字符。", true);
        password.focus();
        return;
      }
      resetButton.disabled = true;
      try {
        await request(`/api/admin/users/${user.id}/password`, {
          method: "PATCH",
          body: JSON.stringify({ password: password.value })
        });
        password.value = "";
        showUserMessage(`已更新 ${user.name} 的密码，旧会话已失效。`);
      } catch (error) {
        showUserMessage(error instanceof Error ? error.message : "密码重置失败", true);
      } finally {
        resetButton.disabled = false;
      }
    });
    reset.append(password, resetButton);

    const toggle = document.createElement("button");
    toggle.type = "button";
    toggle.className = "user-status-button";
    toggle.textContent = user.status === "active" ? "停用账号" : "恢复账号";
    toggle.addEventListener("click", async () => {
      toggle.disabled = true;
      try {
        await request(`/api/admin/users/${user.id}/status`, {
          method: "PATCH",
          body: JSON.stringify({
            status: user.status === "active" ? "disabled" : "active"
          })
        });
        await loadUsers();
        showUserMessage(
          user.status === "active" ? `已停用 ${user.name}` : `已恢复 ${user.name}`
        );
      } catch (error) {
        showUserMessage(error instanceof Error ? error.message : "状态更新失败", true);
      } finally {
        toggle.disabled = false;
      }
    });

    row.append(identity, role, status, reset, toggle);
    list.append(row);
  }
}

async function openUserManagement() {
  usersDialog.showModal();
  try {
    await loadUsers();
  } catch (error) {
    showUserMessage(error instanceof Error ? error.message : "无法读取用户列表", true);
  }
}

function metricText(sample: Sample) {
  if (sample.metric === null) return sample.metricLabel || "未取得指标";
  const value =
    sample.metric >= 10000
      ? `${(sample.metric / 10000).toFixed(1)}万`
      : sample.metric.toLocaleString("zh-CN");
  return `${sample.metricLabel} ${value}`;
}

function updateSelectedCount() {
  $("#selected-count").textContent = String(selectedIds.size);
}

function renderSamples() {
  sampleGrid.replaceChildren();
  if (samples.length === 0) {
    const empty = document.createElement("p");
    empty.className = "empty-state";
    empty.textContent = "样本库为空，请先执行数据库导入。";
    sampleGrid.append(empty);
    return;
  }

  for (const sample of samples) {
    const label = document.createElement("label");
    label.className = "sample-card";
    label.classList.toggle("is-selected", selectedIds.has(sample.id));

    const checkbox = document.createElement("input");
    checkbox.type = "checkbox";
    checkbox.checked = selectedIds.has(sample.id);
    checkbox.addEventListener("change", () => {
      if (checkbox.checked) selectedIds.add(sample.id);
      else selectedIds.delete(sample.id);
      label.classList.toggle("is-selected", checkbox.checked);
      updateSelectedCount();
    });

    const image = document.createElement("img");
    image.loading = "lazy";
    image.alt = `参考封面：${sample.coverCopy || sample.title}`;
    image.src = `/api/samples/${encodeURIComponent(sample.id)}/cover`;
    image.addEventListener("error", () => {
      image.removeAttribute("src");
      image.alt = "封面暂不可用";
    });

    const copy = document.createElement("div");
    copy.className = "sample-card-copy";
    const title = document.createElement("b");
    title.textContent = sample.title || "未取得发布标题";
    const meta = document.createElement("small");
    meta.textContent = `${sample.platform} · ${sample.visualGroup ?? "未分类"} · ${metricText(sample)}`;
    copy.append(title, meta);
    label.append(checkbox, image, copy);
    sampleGrid.append(label);
  }
  updateSelectedCount();
}

async function loadSamples() {
  setStatus("正在读取样本库……");
  try {
    const payload = await request<{ items: Sample[] }>(
      "/api/samples?limit=100&curated=true"
    );
    samples = payload.items.filter((sample) => sample.coverObjectKey);
    if (samples.length === 0) {
      setStatus("样本库还没有导入数据。请运行 npm run db:seed 和 npm run db:import-assets。", true);
    } else if (selectedIds.size === 0) {
      selectedIds.add(samples[0]!.id);
      setStatus("已默认选择第一组参考。你可以更换，也可以只选一组开始。");
    } else {
      setStatus("样本已更新。");
    }
    renderSamples();
  } catch (error) {
    if (error instanceof AuthenticationRequired) {
      showLogin();
      return;
    }
    setStatus(error instanceof Error ? error.message : "读取样本失败", true);
  }
}

async function checkHealth() {
  try {
    const health = await request<{
      ok: boolean;
      database: string;
      objectStorage: string;
    }>("/api/health");
    healthLabel.textContent = health.ok ? "服务与存储正常" : "依赖服务异常";
    healthDot.classList.toggle("is-ok", health.ok);
    healthDot.classList.toggle("is-error", !health.ok);
  } catch {
    healthLabel.textContent = "服务未连接";
    healthDot.classList.add("is-error");
  }
}

function renderResults(job: GenerationJob) {
  results.replaceChildren();
  if (job.candidates.length === 0) {
    const empty = document.createElement("div");
    empty.className = "empty-state result-empty";
    empty.innerHTML =
      '<span class="empty-mark">⌁</span><h3>还没有可用校样</h3><p>任务完成或失败原因会显示在左侧状态栏。</p>';
    results.append(empty);
    return;
  }

  for (const [index, candidate] of job.candidates.entries()) {
    const article = document.createElement("article");
    article.className = "result-card";

    const titleSide = document.createElement("div");
    const label = document.createElement("span");
    label.className = "result-label";
    label.textContent = `TITLE / ${String(index + 1).padStart(2, "0")}`;
    const title = document.createElement("h3");
    title.className = "result-title";
    const editedTitle =
      typeof candidate.edited.title === "string"
        ? candidate.edited.title
        : candidate.title;
    const editedCover =
      typeof candidate.edited.coverCopy === "string"
        ? candidate.edited.coverCopy
        : candidate.coverCopy;
    title.textContent = editedTitle;
    const rationale = document.createElement("p");
    rationale.className = "result-rationale";
    rationale.textContent = candidate.clickReason;
    const evidence = document.createElement("blockquote");
    evidence.className = "result-evidence";
    evidence.textContent = candidate.evidence[0] ?? "";
    const meta = document.createElement("div");
    meta.className = "result-meta";
    const fit = document.createElement("span");
    fit.textContent = candidate.fit;
    const source = document.createElement("span");
    source.textContent = candidate.sourceId;
    meta.append(fit, source);
    if (candidate.riskNotes.length) {
      const risk = document.createElement("span");
      risk.textContent = `待核对：${candidate.riskNotes.join("；")}`;
      meta.append(risk);
    }
    titleSide.append(label, title, rationale, evidence, meta);

    const coverSide = document.createElement("div");
    const coverLabel = document.createElement("span");
    coverLabel.className = "result-label";
    coverLabel.textContent = "COVER / 封面";
    const cover = document.createElement("div");
    cover.className = "cover-copy";
    cover.textContent = editedCover;
    coverSide.append(coverLabel, cover);
    article.append(titleSide, coverSide);

    const review = document.createElement("details");
    review.className = "review-tools";
    const summary = document.createElement("summary");
    summary.textContent = "评分、选用或修改这组校样";

    const tools = document.createElement("div");
    tools.className = "review-grid";
    const stars = document.createElement("div");
    stars.className = "review-stars";
    stars.setAttribute("aria-label", "评分");
    for (let value = 1; value <= 5; value += 1) {
      const star = document.createElement("button");
      star.type = "button";
      star.textContent = "★";
      star.classList.toggle("is-on", value <= candidate.rating);
      star.setAttribute("aria-label", `${value} 星`);
      star.addEventListener("click", async () => {
        candidate.rating = candidate.rating === value ? 0 : value;
        await saveFeedback(candidate, { title: titleInput.value, coverCopy: coverInput.value });
        renderResults(job);
      });
      stars.append(star);
    }
    const choose = document.createElement("button");
    choose.type = "button";
    choose.className = "choose-button";
    choose.classList.toggle("is-selected", candidate.selected);
    choose.textContent = candidate.selected ? "已选用" : "选用这组";
    choose.addEventListener("click", async () => {
      candidate.selected = !candidate.selected;
      choose.disabled = true;
      await saveFeedback(candidate, { title: titleInput.value, coverCopy: coverInput.value });
      renderResults(job);
    });

    const titleInput = document.createElement("textarea");
    titleInput.className = "review-input";
    titleInput.rows = 2;
    titleInput.value = editedTitle;
    titleInput.setAttribute("aria-label", "修改发布标题");
    const coverInput = document.createElement("textarea");
    coverInput.className = "review-input";
    coverInput.rows = 2;
    coverInput.value = editedCover;
    coverInput.setAttribute("aria-label", "修改封面文案");
    const save = document.createElement("button");
    save.type = "button";
    save.className = "save-review";
    save.textContent = "保存人工修改";
    save.addEventListener("click", async () => {
      save.disabled = true;
      try {
        await saveFeedback(candidate, {
          title: titleInput.value,
          coverCopy: coverInput.value
        });
        title.textContent = titleInput.value;
        cover.textContent = coverInput.value;
        save.textContent = "已保存";
      } catch (error) {
        setStatus(error instanceof Error ? error.message : "保存失败", true);
      } finally {
        save.disabled = false;
      }
    });

    const feedbackRow = document.createElement("div");
    feedbackRow.className = "feedback-row";
    feedbackRow.append(stars, choose);
    tools.append(
      feedbackRow,
      titleInput,
      coverInput,
      save
    );
    review.append(summary, tools);
    article.append(review);
    results.append(article);
  }
}

async function saveFeedback(
  candidate: Candidate,
  edited: { title: string; coverCopy: string }
) {
  const payload = buildFeedbackPayload({
    rating: candidate.rating,
    selected: candidate.selected,
    original: { title: candidate.title, coverCopy: candidate.coverCopy },
    edited
  });
  await request(`/api/candidates/${candidate.id}/feedback`, {
    method: "POST",
    body: JSON.stringify(payload)
  });
  candidate.edited = payload.edited;
}

async function pollJob(jobId: string) {
  const started = Date.now();
  while (Date.now() - started < 240_000) {
    const job = await request<GenerationJob>(`/api/generations/${jobId}`);
    if (job.status === "queued") {
      setStatus("任务已进入队列，等待 Worker 领取……");
    } else if (job.status === "running") {
      setStatus("正在核对脚本事实并生成封面与标题……");
    } else if (job.status === "complete") {
      setStatus(`生成完成，共 ${job.candidates.length} 组校样。`);
      renderResults(job);
      return;
    } else {
      setStatus(job.error || "生成未完成，请检查服务日志。", true);
      renderResults(job);
      return;
    }
    await new Promise((resolve) => setTimeout(resolve, 900));
  }
  setStatus("等待超时。任务可能仍在后台运行，请稍后刷新。", true);
}

$("#script").addEventListener("input", (event) => {
  const target = event.currentTarget as HTMLTextAreaElement;
  $("#character-count").textContent = target.value.length.toLocaleString("zh-CN");
});

$<HTMLFormElement>("#generation-form").addEventListener("submit", async (event) => {
  event.preventDefault();
  if (selectedIds.size === 0) {
    setStatus("至少选择一组参考封面。", true);
    return;
  }
  const brief: GenerationBrief = {
    client: $<HTMLSelectElement>("#client").value,
    platform: $<HTMLSelectElement>("#platform").value,
    audience: $<HTMLSelectElement>("#audience").value,
    tone: $<HTMLSelectElement>("#tone").value
  };
  const payload = buildGenerationPayload({
    script: $<HTMLTextAreaElement>("#script").value,
    brief,
    selectedIds: [...selectedIds]
  });
  if (payload.script.length < 40) {
    setStatus("脚本至少需要 40 字。", true);
    return;
  }
  generateButton.disabled = true;
  try {
    setStatus("正在提交生成任务……");
    const job = await request<{ id: string }>("/api/generations", {
      method: "POST",
      body: JSON.stringify(payload)
    });
    await pollJob(job.id);
  } catch (error) {
    if (error instanceof AuthenticationRequired) {
      showLogin();
      return;
    }
    setStatus(error instanceof Error ? error.message : "提交失败", true);
  } finally {
    generateButton.disabled = false;
  }
});

$("#refresh-samples").addEventListener("click", () => void loadSamples());
$("#login-button").addEventListener("click", showLogin);
$("#users-button").addEventListener("click", () => void openUserManagement());
$("#close-users").addEventListener("click", () => usersDialog.close());
$<HTMLFormElement>("#create-user-form").addEventListener("submit", async (event) => {
  event.preventDefault();
  const form = event.currentTarget as HTMLFormElement;
  const submit = form.querySelector<HTMLButtonElement>(
    'button[type="submit"]'
  );
  if (submit) submit.disabled = true;
  try {
    const created = await request<ManagedUser>("/api/admin/users", {
      method: "POST",
      body: JSON.stringify({
        name: $<HTMLInputElement>("#new-user-name").value,
        phone: $<HTMLInputElement>("#new-user-phone").value,
        password: $<HTMLInputElement>("#new-user-password").value,
        role: $<HTMLSelectElement>("#new-user-role").value
      })
    });
    form.reset();
    await loadUsers();
    showUserMessage(`已创建账号：${created.name} · ${created.phone}`);
  } catch (error) {
    showUserMessage(error instanceof Error ? error.message : "创建账号失败", true);
  } finally {
    if (submit) submit.disabled = false;
  }
});
$("#logout-button").addEventListener("click", async () => {
  try {
    await request("/api/auth/logout", { method: "POST" });
    window.location.reload();
  } catch (error) {
    setStatus(error instanceof Error ? error.message : "退出失败", true);
  }
});
$<HTMLFormElement>("#login-form").addEventListener("submit", async (event) => {
  event.preventDefault();
  const errorNode = $<HTMLElement>("#login-error");
  errorNode.hidden = true;
  try {
    await request<CurrentUser>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({
        phone: $<HTMLInputElement>("#phone").value,
        password: $<HTMLInputElement>("#password").value
      })
    }, true);
    loginDialog.close();
    await loadCurrentUser();
    await checkHealth();
    await loadSamples();
  } catch (error) {
    errorNode.textContent = error instanceof Error ? error.message : "登录失败";
    errorNode.hidden = false;
  }
});

void checkHealth();
void loadCurrentUser();
void loadSamples();
